import {
  IActivationCustomField,
  IBarcode,
  IFormInputSchema,
  ILabelDisplay,
  IProject,
  ITrackingAuditData,
} from '../utils/types/index'
import { MASKED_ANNOTATION, OTHER_COUNTRY_LIST, MASKED_ADDRESS, MASKED_PACKAGE_DIMENSION } from './constants'
import { ECustomFieldType, ECustomFieldUnitPosition } from './enums/index'
import { mask } from 'maska'
import get from 'lodash/get'
import { ISelectionItem } from './types/model/fieldData'

/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-unused-expressions */

function compileOriginData(barcode: IBarcode) {
  if (barcode?.activationData?.maskedFields?.includes('origin')) {
    barcode.activationData.origin = MASKED_ADDRESS
  }
}
function compileDestinationData(barcode: IBarcode) {
  if (barcode?.activationData?.maskedFields?.includes('origin')) {
    barcode.activationData.destination = MASKED_ADDRESS
  }
}

function compilePackageInfoData(barcode: IBarcode) {
  if (barcode.activationData?.maskedFields?.includes('dimension')) {
    barcode.activationData.dimension = {
      ...MASKED_PACKAGE_DIMENSION,
    }
  }

  if (barcode.activationData?.maskedFields?.includes('trackingNumber')) {
    barcode.activationData.trackingNumber = MASKED_ANNOTATION
  }

  if (barcode.activationData?.maskedFields?.includes('externalId')) {
    barcode.activationData.externalId = MASKED_ANNOTATION
  }
}

function compileCustomFieldData(customFields: unknown, maskedFields: string[], definition: unknown) {
  if (maskedFields) {
    const keys = maskedFields.filter((maskField) => maskField.match(/^customFields\./))
    keys.forEach((pathKey) => {
      const field: IFormInputSchema = get(definition, pathKey)
      const key = pathKey.split('.')[pathKey.split('.').length - 1]
      if (!customFields) return
      switch (field?.fieldType) {
        case ECustomFieldType.TEXT:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.TEXT,
              [ECustomFieldType.TEXT]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.DATE:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.DATE,
              [ECustomFieldType.DATE]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.EMAIL:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.EMAIL,
              [ECustomFieldType.EMAIL]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.PHONE_NUMBER: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.PHONE_NUMBER,
              [ECustomFieldType.PHONE_NUMBER]: {
                countryCode: MASKED_ANNOTATION,
                number: MASKED_ANNOTATION,
              },
            },
          })
          break
        }
        case ECustomFieldType.NUMBER:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.NUMBER,
              [ECustomFieldType.NUMBER]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.MULTI_SELECT: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.MULTI_SELECT,
              [ECustomFieldType.MULTI_SELECT]: [{ key: MASKED_ANNOTATION, value: MASKED_ANNOTATION }],
            },
          })
          break
        }
        case ECustomFieldType.FILES: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.FILES,
              [ECustomFieldType.FILES]: [{ name: MASKED_ANNOTATION, path: MASKED_ANNOTATION, url: MASKED_ANNOTATION }],
            },
          })
          break
        }
        case ECustomFieldType.SINGLE_SELECT: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.SINGLE_SELECT,
              [ECustomFieldType.SINGLE_SELECT]: { key: MASKED_ANNOTATION, value: MASKED_ANNOTATION },
            },
          })
          break
        }
        case ECustomFieldType.REFERENCE: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.REFERENCE,
              [ECustomFieldType.REFERENCE]: {
                template: MASKED_ANNOTATION,
                [ECustomFieldType.TEXT]: MASKED_ANNOTATION,
              },
            },
          })
          break
        }
        case ECustomFieldType.ESIGN: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.ESIGN,
              [ECustomFieldType.ESIGN]: MASKED_ANNOTATION,
            },
          })
          break
        }
        case ECustomFieldType.GHG_EMISSION: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.GHG_EMISSION,
              [ECustomFieldType.GHG_EMISSION]: MASKED_ANNOTATION,
            },
          })
          break
        }
      }
    })
  }
}

function compileActivationDataCustomFields(barcode: IBarcode, projectDetail: IProject) {
  const currentBarcodeDefinition = projectDetail?.barcodeTypes[barcode?.barcodeType]
  if (!currentBarcodeDefinition) return
  compileCustomFieldData(
    barcode.activationData.customFields,
    barcode.activationData.maskedFields || [],
    currentBarcodeDefinition.activationFields
  )
  barcode.activationData.linkedBarcodes?.forEach((barcode) => {
    compileLinkedData(barcode, projectDetail)
  })
}

function compileTrackingDataAudit(maskedFields: string[], auditData?: ITrackingAuditData) {
  if (auditData && maskedFields) {
    if (maskedFields.includes('trackedUserName')) {
      auditData.trackedUserName = MASKED_ANNOTATION
    }
    if (maskedFields.includes('trackedUserId')) {
      auditData.trackedUserId = MASKED_ANNOTATION
    }
  }
}

function compileTrackingData(barcode: IBarcode, projectDetail: IProject) {
  const trackingData = barcode?.trackingData || []
  trackingData.forEach((td, idx) => {
    const currentTPKey = td?.currentTrackPointKey
    const nextTPKey = td?.nextTrackPointKey
    const tpDefinition = projectDetail?.trackPoints?.[currentTPKey]?.trackPointForms?.[nextTPKey]
    compileCustomFieldData(td?.customFields, td?.maskedFields as string[], tpDefinition)
    compileTrackingDataAudit(td?.maskedFields as string[], td?.audit)
    trackingData?.[idx]?.linkedBarcodes?.forEach((barcode) => {
      compileLinkedData(barcode, projectDetail)
    })
  })
}

export function getCompiledBarcode(barcode: IBarcode, project: IProject) {
  compileOriginData(barcode)
  compileDestinationData(barcode)
  compilePackageInfoData(barcode)
  compileActivationDataCustomFields(barcode, project)
  compileTrackingData(barcode, project)
  return barcode
}

function compileLinkedData(linkedBarcodes: IBarcode, project: IProject) {
  if (!linkedBarcodes) return
  getCompiledBarcode(linkedBarcodes, project)
}
export function parseCustomFieldsValue(
  customFields: Record<string, ILabelDisplay>,
  customFieldsDefinition: Record<string, IActivationCustomField>,
  barcode: IBarcode
) {
  Object.entries(customFieldsDefinition)
    .sort((a, b) => (a?.[1]?.order && b?.[1]?.order ? a[1].order - b[1].order : 1))
    .forEach(([key, customField]) => {
      const customFieldItem = barcode?.activationData?.customFields?.[key]
      if (customFieldItem && customField.label) {
        if (customField.isHidden || !customField.label) return
        customFields[key] = {
          value: '',
          label: customField.label,
          fieldType: customField.fieldType,
        }
        switch (customField.fieldType) {
          case ECustomFieldType.TEXT:
            if (customFieldItem?.text) {
              customFields[key].value = customFieldItem.text
            }
            break
          case ECustomFieldType.DATE:
            if (customFieldItem?.date) {
              customFields[key].value = customFieldItem.date
            }
            break
          case ECustomFieldType.DATE_TIME_DATA:
            if (customFieldItem?.dateTimeData) {
              customFields[key].value = customFieldItem.dateTimeData
            }
            break
          case ECustomFieldType.LIST_STRING:
            if (customFieldItem?.listString) {
              customFields[key].value = customFieldItem.listString
            }
            break
          case ECustomFieldType.EMAIL:
            if (customFieldItem?.email) {
              customFields[key].value = customFieldItem.email
            }
            break
          case ECustomFieldType.PHONE_NUMBER: {
            if (customFieldItem?.phoneNumber) {
              const phoneNo = customFieldItem.phoneNumber
              const dialCode =
                OTHER_COUNTRY_LIST[phoneNo!.countryCode as keyof typeof OTHER_COUNTRY_LIST]?.dialCode ?? ''

              customFields[key].value =
                phoneNo.number === MASKED_ANNOTATION
                  ? MASKED_ANNOTATION
                  : `${dialCode} ${maskPhoneNum(phoneNo.number, phoneNo!.countryCode)}`
            }
            break
          }
          case ECustomFieldType.NUMBER:
            if (customFieldItem?.number) {
              customFields[key].value = parseFloat(customFieldItem.number.toString()).toLocaleString()
            }
            if (
              customField?.position &&
              customField?.position !== ECustomFieldUnitPosition.UNKNOWN &&
              customField.unitType
            ) {
              if (customField.position === ECustomFieldUnitPosition.PREFIX) {
                customFields[key].value = `${customField.unitType} ${customFields[key].value}`
              } else if (customField.position === ECustomFieldUnitPosition.SUFFIX) {
                customFields[key].value = `${customFields[key].value} ${customField.unitType}`
              }
            }
            if (customFieldItem.number?.toString() === MASKED_ANNOTATION) {
              customFields[key].value = MASKED_ANNOTATION
            }
            break
          case ECustomFieldType.ADJUSTMENT:
            if (customFieldItem?.currentValue !== null && customFieldItem?.currentValue !== undefined) {
              customFields[key].value = parseFloat(customFieldItem.currentValue.toString()).toLocaleString()
            }
            if (
              customField?.position &&
              customField?.position !== ECustomFieldUnitPosition.UNKNOWN &&
              customField.unitType
            ) {
              if (customField.position === ECustomFieldUnitPosition.PREFIX) {
                customFields[key].value = `${customField.unitType} ${customFields[key].value}`
              } else if (customField.position === ECustomFieldUnitPosition.SUFFIX) {
                customFields[key].value = `${customFields[key].value} ${customField.unitType}`
              }
            }
            if (customFieldItem.currentValue?.toString() === MASKED_ANNOTATION) {
              customFields[key].value = MASKED_ANNOTATION
            }
            break
          case ECustomFieldType.MULTI_SELECT: {
            const multiSelect = customFieldItem?.multiSelect as ISelectionItem[]
            if (multiSelect) {
              const data: string[] = []
              multiSelect.forEach((element) => {
                if (element.key === MASKED_ANNOTATION) {
                  data.push(MASKED_ANNOTATION)
                } else {
                  data.push(customField.selections ? customField?.selections?.[element?.key]?.label : '')
                }
              })
              customFields[key].value = data.toString()
            }
            break
          }
          case ECustomFieldType.SINGLE_SELECT: {
            const selections = customFieldItem?.[ECustomFieldType.SINGLE_SELECT]
            if (selections && customField?.selections?.[selections?.key]?.label) {
              customFields[key].value = customField?.selections?.[selections?.key]?.label
            }
            if (selections?.key === MASKED_ANNOTATION) {
              customFields[key].value = MASKED_ANNOTATION
            }
            break
          }
          case ECustomFieldType.REFERENCE: {
            const referencedData = customFieldItem?.[ECustomFieldType.REFERENCE]?.text
            customFields[key].value = referencedData || ''
            break
          }
          case ECustomFieldType.CALCULATION: {
            const calculationData = customFieldItem?.[ECustomFieldType.CALCULATION]
            customFields[key].value = calculationData ?? ''
          }
        }
      }
    })
}

export function maskPhoneNum(num: string, countryCode: string) {
  if (!countryCode) {
    return num
  }
  const countryData = OTHER_COUNTRY_LIST[countryCode as keyof typeof OTHER_COUNTRY_LIST]
  const countPhoneNumberDigits = (formatString: string) => (formatString.match(/[#\d]/g) || []).length
  const masks = countryData.mask.sort((e1, e2) => countPhoneNumberDigits(e1) - countPhoneNumberDigits(e2))
  const selectedMask =
    masks.length === 1
      ? masks[0]
      : masks.find((mask: string) => countPhoneNumberDigits(num) <= countPhoneNumberDigits(mask)) ??
        masks[masks.length - 1]

  return mask(num, selectedMask)
}
